/**
 * 注力①②③ + 追加KGI catalog — every KPI in the registry that may hold a target.
 * Gap shortlist is computed from the same snapshot Home shows (no extra BQ).
 */

import { resolveNewReturnFromHistory } from './kpiSnapshot';
import { KPI_BY_FOCUS_KEY, TARGETABLE_ENTRIES, readKpiValue } from './kpiRegistry';

/** @typedef {'percent'|'currency'|'count'|'days'|'pieces'} KpiUnit */

function resolveUnitPriceLocal(kpis, kpiHistory, selectedMonth) {
  if (kpis?.total_sales != null && kpis?.customer_count) {
    const n = Number(kpis.customer_count);
    if (n > 0) return Number(kpis.total_sales) / n;
  }
  if (kpis?.avg_customer_spend != null) return Number(kpis.avg_customer_spend);
  if (!Array.isArray(kpiHistory)) return null;
  const row =
    kpiHistory.find((r) => r.month === selectedMonth || r.ym === selectedMonth) ||
    kpiHistory[kpiHistory.length - 1];
  if (!row?.total_sales) return null;
  const n =
    Number(row.customer_count) ||
    (Number(row.new_customer_count) || 0) + (Number(row.repeat_customer_count) || 0);
  return n > 0 ? Number(row.total_sales) / n : null;
}

/**
 * Derived from the KPI registry — a KPI is selectable as 注力 iff `targetable`.
 * @type {Record<string, { key: string, label: string, icon: string, unit: KpiUnit, chartKey?: string, better: 'up'|'down' }>}
 */
export const FOCUS_KPI_DEFS = Object.fromEntries(
  TARGETABLE_ENTRIES.map((e) => [
    e.focusKey,
    {
      key: e.focusKey,
      label: e.label,
      icon: e.icon,
      unit: e.unit,
      chartKey: e.chartKey || undefined,
      better: e.better,
    },
  ]),
);

export const FOCUS_KPI_KEYS = Object.keys(FOCUS_KPI_DEFS);

/** Keys allowed as 注力 when no 追加KGI is set (売上 is always the KGI). Prefer focusLeverKeysFor(goals). */
export const FOCUS_LEVER_KEYS = FOCUS_KPI_KEYS.filter((k) => k !== 'totalSales');

/** 追加KGI candidates. 売上 is always the KGI; at most one of these rides along with it. */
export const SUB_KGI_KEYS = ['nomination', 'nominationCustomers'];

/** 注力 slots ①②③ and the goals fields each one lives in. */
export const FOCUS_SLOTS = [
  { slot: 1, label: '①', keyField: 'focusKey', targetField: 'focusTarget', scope: 'kpi1' },
  { slot: 2, label: '②', keyField: 'subFocusKey', targetField: 'subFocusTarget', scope: 'kpi2' },
  { slot: 3, label: '③', keyField: 'thirdFocusKey', targetField: 'thirdFocusTarget', scope: 'kpi3' },
];

/**
 * Slots fill in order (① → ② → ③, never a gap): 「追加」 always opens the first empty slot.
 * @param setSlots slot numbers that hold a 注力 now, e.g. [1, 2]
 * @returns 1–3, or null when all three are set
 */
export function nextFocusSlot(setSlots = []) {
  return [1, 2, 3].find((n) => !setSlots.includes(n)) ?? null;
}

export function getFocusDef(key) {
  return FOCUS_KPI_DEFS[key] || null;
}

export function isValidFocusKey(key) {
  return Boolean(key && FOCUS_KPI_DEFS[key]);
}

export function isValidSubKgiKey(key) {
  return Boolean(key && SUB_KGI_KEYS.includes(key));
}

/** The 追加KGI on these goals, or null. Unknown keys are not a selection. */
export function resolveSubKgi(goals) {
  const key = isValidSubKgiKey(goals?.subKgiKey) ? goals.subKgiKey : null;
  if (!key) return null;
  const t = goals?.subKgiTarget;
  const target = t != null && !Number.isNaN(Number(t)) ? Number(t) : null;
  return { key, def: FOCUS_KPI_DEFS[key], target };
}

/** Every KGI key on these goals: 売上 always, plus the 追加KGI when set. */
export function kgiKeysFor(goals) {
  const sub = resolveSubKgi(goals);
  return sub ? ['totalSales', sub.key] : ['totalSales'];
}

/**
 * Keys selectable as 注力 for these goals = every KPI − KGI keys.
 * Pass `{ ...goals, subKgiKey: draft }` to judge against a KGI that is not saved yet.
 */
export function focusLeverKeysFor(goals) {
  const kgi = kgiKeysFor(goals);
  return FOCUS_KPI_KEYS.filter((k) => !kgi.includes(k));
}

/**
 * Drop `key` from the 注力 slots and move the remaining slots up (no gaps), targets travelling
 * with their keys. Used when the key becomes a KGI (KGI wins) and for「この注力を外す」.
 * Reads the RAW goals — resolveFocusSelection hides a 注力 that collides with the KGI, so it
 * cannot tell which stored slot still holds the key.
 * Returns `{ patch, dropped }`; `patch` spells out every slot (nulls included) so a PATCH clears them.
 */
export function dropFocusKey(goals, key) {
  const raw = FOCUS_SLOTS.map((s) => ({
    slot: s,
    key: goals?.[s.keyField] ?? null,
    target: goals?.[s.targetField] ?? null,
  }));
  const dropped = raw.filter((r) => key && r.key === key).map((r) => r.slot);
  if (!dropped.length) return { patch: {}, dropped: [] };
  const kept = raw.filter((r) => r.key && r.key !== key);
  const patch = {};
  FOCUS_SLOTS.forEach((s, i) => {
    patch[s.keyField] = kept[i]?.key ?? null;
    patch[s.targetField] = kept[i]?.target ?? null;
  });
  return { patch, dropped };
}

/**
 * Placeholder for a 注力 slot the user has not chosen yet.
 * Keeps card/row layout intact without naming a KPI nobody selected.
 */
export const UNSELECTED_FOCUS = {
  key: null,
  label: '未選択',
  icon: '➕',
  unit: null,
};

/**
 * Single source of truth for "which 注力 KPIs did the user pick?" (slots ①②③).
 * Returns nulls when nothing valid is saved — 注力 has no default, so callers must
 * branch on isSelected instead of substituting a KPI.
 * A slot is dropped (stays null, never swapped) when it repeats an earlier slot or is the 追加KGI.
 * 売上 is NOT dropped here: older goals may hold totalSales as 注力 and must render as before —
 * it is kept out of new picks by focusLeverKeysFor / the BE instead.
 * `keys` lists the selected keys in slot order.
 */
export function resolveFocusSelection(goals) {
  const subKgiKey = resolveSubKgi(goals)?.key ?? null;
  const taken = [];
  const pick = (raw) => {
    const key = isValidFocusKey(raw) ? raw : null;
    if (!key || taken.includes(key) || key === subKgiKey) return null;
    taken.push(key);
    return key;
  };
  const focusKey = pick(goals?.focusKey);
  const subFocusKey = pick(goals?.subFocusKey);
  const thirdFocusKey = pick(goals?.thirdFocusKey);
  return {
    focusKey,
    subFocusKey,
    thirdFocusKey,
    keys: [focusKey, subFocusKey, thirdFocusKey].filter(Boolean),
    focusDef: focusKey ? FOCUS_KPI_DEFS[focusKey] : null,
    subFocusDef: subFocusKey ? FOCUS_KPI_DEFS[subFocusKey] : null,
    thirdFocusDef: thirdFocusKey ? FOCUS_KPI_DEFS[thirdFocusKey] : null,
    isSelected: focusKey != null,
    isSubSelected: subFocusKey != null,
    isThirdSelected: thirdFocusKey != null,
  };
}

/** Current value for a focus key from Home kpis (+ history fallback for NRR/客単価). */
export function readFocusActual(key, kpis, kpiHistory, selectedMonth) {
  const entry = KPI_BY_FOCUS_KEY[key];
  if (!entry) return null;

  if (key === 'newReturn') {
    const v = readKpiValue(entry, kpis);
    if (v != null) return v;
    return resolveNewReturnFromHistory(kpiHistory, selectedMonth)?.value ?? null;
  }
  if (key === 'unitPrice') {
    const v = resolveUnitPriceLocal(kpis, kpiHistory, selectedMonth);
    return v != null && !Number.isNaN(Number(v)) ? Math.round(Number(v)) : null;
  }
  return readKpiValue(entry, kpis);
}

/**
 * Soft "healthy" benchmarks for gap scoring (not hard goals) — `benchmark` in the registry.
 * Higher gapScore = more room to improve / weaker vs benchmark.
 */
function gapScore(key, actual, sales) {
  if (actual == null || Number.isNaN(Number(actual))) return -1;
  const a = Number(actual);
  const entry = KPI_BY_FOCUS_KEY[key];
  if (!entry) return 0;
  if (entry.unit === 'percent' && entry.benchmark != null) {
    return Math.max(0, entry.benchmark - a); // pt below good
  }
  if (key === 'retailSales' && sales > 0) {
    const share = (a / sales) * 100;
    return Math.max(0, 15 - share); // want ~15%+ retail share
  }
  if (key === 'unitPrice' && sales > 0 && entry.benchmark != null) {
    // Prefer lifting unit price when absolute is modest vs sales scale
    return Math.max(0, entry.benchmark - a) / 1000;
  }
  if (entry.unit === 'count' && entry.benchmark != null) {
    return Math.max(0, entry.benchmark - a);
  }
  return 0; // no benchmark → never recommended, still selectable
}

/**
 * Never auto-recommended as 注力, whatever the gap says.
 *
 * 新規再来率 is a 3-month-lagged cohort computed per stylist per DAY, so the
 * denominator is usually 1–3 customers: 80% of daily rows are NULL and the rest
 * are almost all exactly 0 or 100. The monthly rollup then weights that rate by
 * the *current* month's new-customer count, a different cohort. A 0% reading is
 * therefore not evidence of a real gap. Still selectable by hand.
 */
export const NEVER_RECOMMEND_FOCUS_KEYS = ['newReturn'];

/** Below this, 指名率 is proposed first regardless of its raw gap score. */
export const NOMINATION_PRIORITY_BELOW = 80;

/**
 * Rank Home KPIs by gap; returns defs with actual + score (desc).
 * Two business rules override the raw score — see the constants above.
 * KGI keys (売上 + the 追加KGI from `goals`) are skipped. With 指名率 as the 追加KGI it never
 * enters `rows`, so the 指名率-first rule below has nothing to promote.
 */
export function rankFocusGaps(kpis, kpiHistory, selectedMonth, goals = null) {
  const sales = readFocusActual('totalSales', kpis, kpiHistory, selectedMonth) || 0;
  const levers = focusLeverKeysFor(goals);
  const rows = [];
  for (const key of FOCUS_KPI_KEYS) {
    if (!levers.includes(key)) continue; // KGI owns it; keep focus on levers
    if (NEVER_RECOMMEND_FOCUS_KEYS.includes(key)) continue;
    const def = FOCUS_KPI_DEFS[key];
    const actual = readFocusActual(key, kpis, kpiHistory, selectedMonth);
    const score = gapScore(key, actual, sales);
    if (score < 0) continue; // no data
    rows.push({ ...def, actual, score });
  }
  rows.sort((a, b) => b.score - a.score);

  // 指名率 outranks everything while it is under the healthy line: it is the
  // lever the salon acts on first, and every other KPI moves with it.
  const i = rows.findIndex((r) => r.key === 'nomination');
  const nominationLeads =
    i >= 0 && rows[i].actual != null && Number(rows[i].actual) < NOMINATION_PRIORITY_BELOW;
  if (nominationLeads) {
    if (i > 0) rows.unshift(rows.splice(i, 1)[0]);
    // Pair it with 指名客数. 指名率 is a mix ratio: on its own it moves no money,
    // because 売上 = 客数 × 客単価 and a ratio changes neither. The count is what
    // actually feeds 客数, so the two belong together as ①②.
    const j = rows.findIndex((r) => r.key === 'nominationCustomers');
    if (j > 1) rows.splice(1, 0, rows.splice(j, 1)[0]);
  }
  return rows;
}

/**
 * Top 2 focus KPIs by gap — a *recommendation*, never a selection.
 * Returns fewer than 2 (or none) when the snapshot has no data: 注力 has no default,
 * so an empty shortlist must stay empty instead of falling back to NRR + 客単価.
 */
export function pickFocusPairFromGaps(kpis, kpiHistory, selectedMonth) {
  return rankFocusGaps(kpis, kpiHistory, selectedMonth).slice(0, 2);
}

/** One-line shortlist for kpi_summary / 【目標相談】. `goals` keeps KGI keys out of it. */
export function formatGapCandidatesLine(kpis, kpiHistory, selectedMonth, limit = 5, goals = null) {
  const ranked = rankFocusGaps(kpis, kpiHistory, selectedMonth, goals).slice(0, limit);
  if (!ranked.length) return 'GAP候補: データ不足（ホームPOSを確認）';
  const parts = ranked.map((r, i) => {
    const size = i === 0 ? '大' : i === 1 ? '中' : '小';
    const cur =
      r.actual == null
        ? '未取得'
        : r.unit === 'percent'
          ? `${Number(r.actual).toFixed(1)}%`
          : r.unit === 'currency'
            ? `¥${Math.round(r.actual).toLocaleString('ja-JP')}`
            : String(Math.round(r.actual));
    return `${r.key}(${r.label}:${cur}/${size})`;
  });
  return `GAP候補（ホーム実績・穴が大きい順・注力（1〜3つ）はここから選ぶ）: ${parts.join(' ')}`;
}

export function formatFocusValue(unit, value) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  if (unit === 'percent') return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
  if (unit === 'currency') return `¥${Math.round(n).toLocaleString('ja-JP')}`;
  return String(Math.round(n));
}

/** Map focus key → chartTargets field (partial). */
export function chartKeyForFocus(focusKey) {
  return FOCUS_KPI_DEFS[focusKey]?.chartKey || null;
}
